// src/pages/About.js
import React from "react";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  LuLeaf,
  LuHeartPulse,
  LuSun,
  LuUsers,
  LuGlobe,
  LuSprout,
  LuBookOpen,
  LuInfinity,
  LuWind,
  LuHeartHandshake,
  LuCheck,
  LuCompass,
} from "react-icons/lu";
import SEO from '../components/SEO';
import heroImage from '../assets/aboutUs/hero-solarpunk-city.webp';
import missionImage from '../assets/aboutUs/mission-commons.webp';
import workImage from '../assets/aboutUs/work-studio.webp';
import ctaImage from '../assets/aboutUs/cta-valley-sunset.webp';

// Small helper so every section shares the same scroll-reveal behaviour.
const useReveal = (threshold = 0.2) =>
  useInView({ triggerOnce: true, threshold, rootMargin: "-50px" });

const Eyebrow = ({ children }) => (
  <p className="text-moss-light text-sm font-medium tracking-[0.2em] uppercase mb-4">
    {children}
  </p>
);

const About = () => {
  const { t } = useTranslation();

  // Colors for consistent theming
  const primaryTextColor = "text-silver-light"; // Very light gray/silver
  const secondaryTextColor = "text-silver-medium"; // Medium gray/silver

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [introRef, introInView] = useReveal();
  const [solarpunkRef, solarpunkInView] = useReveal();
  const [beliefsRef, beliefsInView] = useReveal();
  const [missionRef, missionInView] = useReveal();
  const [whatWeDoRef, whatWeDoInView] = useReveal();
  const [valuesRef, valuesInView] = useReveal();
  const [impactRef, impactInView] = useReveal();
  const [ctaRef, ctaInView] = useReveal();

  const pillars = [
    {
      Icon: LuLeaf,
      title: t('about.pillars.nature.title', 'Nature First'),
      text: t('about.pillars.nature.text', 'We protect and restore our planet in every project we take on.'),
    },
    {
      Icon: LuHeartPulse,
      title: t('about.pillars.health.title', 'Cure & Health'),
      text: t('about.pillars.health.text', 'We work toward a world free from disease and human suffering.'),
    },
    {
      Icon: LuSun,
      title: t('about.pillars.abundance.title', 'Abundance for All'),
      text: t('about.pillars.abundance.text', 'We create systems that produce, regenerate, and distribute abundance.'),
    },
    {
      Icon: LuUsers,
      title: t('about.pillars.peace.title', 'Peace & Harmony'),
      text: t('about.pillars.peace.text', 'We believe in collaboration, respect, and unity across all boundaries.'),
    },
  ];

  const solarpunkGoals = [
    t('about.solarpunk.goal1', 'Curing cancer and eradicating disease'),
    t('about.solarpunk.goal2', 'Restoring ecosystems and biodiversity'),
    t('about.solarpunk.goal3', 'Clean energy and regenerative production'),
    t('about.solarpunk.goal4', 'Abundant crops and food security for all'),
    t('about.solarpunk.goal5', 'Education, empowerment, and open knowledge'),
    t('about.solarpunk.goal6', 'Peace between humans, nature, and machine'),
  ];

  const beliefs = [
    {
      Icon: LuGlobe,
      title: t('about.beliefs.regeneration.title', 'Regeneration'),
      text: t('about.beliefs.regeneration.text', 'We leave every system better than we found it.'),
    },
    {
      Icon: LuSprout,
      title: t('about.beliefs.innovation.title', 'Responsible Innovation'),
      text: t('about.beliefs.innovation.text', 'We innovate with ethics, transparency, and long-term impact in mind.'),
    },
    {
      Icon: LuUsers,
      title: t('about.beliefs.humanity.title', 'Humanity First'),
      text: t('about.beliefs.humanity.text', 'Technology should serve people, communities, and all living beings.'),
    },
    {
      Icon: LuBookOpen,
      title: t('about.beliefs.open.title', 'Open & Empowered'),
      text: t('about.beliefs.open.text', 'We share knowledge and empower others to build a better world.'),
    },
    {
      Icon: LuInfinity,
      title: t('about.beliefs.longterm.title', 'Long-Term Thinking'),
      text: t('about.beliefs.longterm.text', 'We design for generations, not just for today.'),
    },
  ];

  const capabilities = [
    t('about.whatWeDo.item1', 'Web & Mobile Applications'),
    t('about.whatWeDo.item2', 'Platform & Infrastructure Development'),
    t('about.whatWeDo.item3', 'Cloud & DevOps Solutions'),
    t('about.whatWeDo.item4', 'AI & Automation'),
    t('about.whatWeDo.item5', 'Data, Security & Scalability'),
  ];

  const impact = [
    {
      Icon: LuLeaf,
      title: t('about.impact.environment.title', 'Environment'),
      text: t('about.impact.environment.text', 'We support projects that restore nature and reduce our footprint.'),
    },
    {
      Icon: LuHeartPulse,
      title: t('about.impact.health.title', 'Health'),
      text: t('about.impact.health.text', 'We contribute to the future of medicine, research, and global health.'),
    },
    {
      Icon: LuSun,
      title: t('about.impact.communities.title', 'Communities'),
      text: t('about.impact.communities.text', 'We build tools that empower local communities and creators everywhere.'),
    },
    {
      Icon: LuWind,
      title: t('about.impact.energy.title', 'Clean Energy'),
      text: t('about.impact.energy.text', 'We support the transition to renewable, decentralized energy systems.'),
    },
    {
      Icon: LuHeartHandshake,
      title: t('about.impact.collaboration.title', 'Collaboration'),
      text: t('about.impact.collaboration.text', 'We partner with visionaries who share our commitment to a better future.'),
    },
  ];

  return (
    <div className="bg-black min-h-screen font-sans">
      <SEO
        title="About Riviera Tech Solutions | Custom Website Developer in Croatia"
        description="Learn how Riviera Tech Solutions builds custom websites and web applications for businesses across Croatia. We focus on SEO-first builds, secure architecture, and long-term happy customers."
        keywords="custom website developer Croatia, custom web developer Zadar, web application development company Croatia, website developer for business, about Riviera Tech Solutions"
        url="/about"
      />

      {/* Hero Section — full-bleed solarpunk skyline.
          `-mt-16` cancels the global pt-16 that App.js puts on <main> to clear
          the fixed navbar; this hero deliberately runs UNDER the transparent
          bar instead, so the image reaches the top of the viewport. */}
      <section ref={heroRef} className="relative -mt-16">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          {/* Two-axis scrim: dark enough to read the headline, light enough on
              the right that the city itself still carries the section.
              On narrow screens the copy spans the full width, so the
              left-to-right fade alone leaves it sitting on bright sky — hence
              the flat base layer below md. */}
          <div className="absolute inset-0 bg-black/50 md:hidden" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>

        <div className="relative z-10 container mx-auto px-4 pt-40 pb-16 md:pt-52 md:pb-28">
          <div
            className={`max-w-2xl transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <Eyebrow>{t('about.hero.eyebrow', 'About Us')}</Eyebrow>
            <h1 className={`text-4xl md:text-6xl font-serif font-medium leading-tight mb-6 ${primaryTextColor}`}>
              {t('about.hero.title', 'Technology to Heal. To Produce. To Elevate Life.')}
            </h1>
            <p className={`text-lg md:text-xl ${secondaryTextColor} mb-10 leading-relaxed`}>
              {t('about.hero.subtitle', 'At Riviera Tech Solutions we build technology that serves a higher purpose: a healthier planet, abundant communities, and a future where everyone can thrive.')}
            </p>
            <a
              href="#mission"
              className="inline-flex items-center gap-2 bg-moss-deep hover:bg-moss text-silver-light px-6 py-3 rounded-md font-medium transition-colors duration-300"
            >
              <LuCompass className="w-5 h-5" aria-hidden="true" />
              {t('about.hero.cta', 'Our Mission')}
            </a>
          </div>
        </div>

        {/* Pillar strip — overlaps the hero on desktop, stacks below on mobile */}
        <div className="relative z-10 container mx-auto px-4 pb-16 md:pb-20">
          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 rounded-lg overflow-hidden border border-moss-deep/60 bg-moss-night/90 backdrop-blur-sm">
            {pillars.map(({ Icon, title, text }, i) => (
              <div
                key={title}
                className={`p-6 flex gap-4 ${i > 0 ? "lg:border-l border-moss-deep/50" : ""}`}
              >
                <Icon className="w-6 h-6 text-moss-light flex-shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <h3 className={`font-medium mb-1 ${primaryTextColor}`}>{title}</h3>
                  <p className={`text-sm leading-relaxed ${secondaryTextColor}`}>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Building a Solarpunk Future */}
      <section id="mission" ref={solarpunkRef} className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div
            className={`max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12 transition-all duration-1000 transform ${solarpunkInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="w-full lg:w-1/2">
              <Eyebrow>{t('about.solarpunk.eyebrow', 'Our Mission')}</Eyebrow>
              <h2 className={`text-3xl md:text-4xl font-serif font-medium mb-6 ${primaryTextColor}`}>
                {t('about.solarpunk.title', 'Building a Solarpunk Future')}
              </h2>
              <p className={`${secondaryTextColor} mb-6 leading-relaxed`}>
                {t('about.solarpunk.paragraph1', 'We envision a world where technology and nature exist in harmony — where innovation is used to solve humanity’s greatest challenges, from curing disease to restoring ecosystems, from clean energy to food abundance.')}
              </p>
              <p className={`${secondaryTextColor} mb-8 leading-relaxed`}>
                {t('about.solarpunk.paragraph2', 'We build digital solutions and platforms that empower businesses, creators, and communities to regenerate the world and uplift all of life.')}
              </p>
              <ul className="space-y-3">
                {solarpunkGoals.map((goal) => (
                  <li key={goal} className={`flex items-start gap-3 ${secondaryTextColor}`}>
                    <LuCheck className="w-5 h-5 text-moss flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{goal}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-1/2">
              <img
                src={missionImage}
                alt="A shared garden terrace at golden hour, tables of people eating together beneath green towers"
                className="w-full rounded-lg border border-moss-deep/50 shadow-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Beliefs — The Future We Build */}
      <section ref={beliefsRef} className="py-20 bg-moss-night border-y border-moss-deep/40">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Eyebrow>{t('about.beliefs.eyebrow', 'Our Beliefs')}</Eyebrow>
            <h2 className={`text-3xl md:text-4xl font-serif font-medium ${primaryTextColor}`}>
              {t('about.beliefs.title', 'The Future We Build')}
            </h2>
          </div>
          <div
            className={`max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 transition-all duration-1000 transform ${beliefsInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            {beliefs.map(({ Icon, title, text }) => (
              <div key={title} className="text-center px-2">
                <Icon className="w-9 h-9 text-moss-light mx-auto mb-4" aria-hidden="true" />
                <h3 className={`font-medium mb-2 ${primaryTextColor}`}>{title}</h3>
                <p className={`text-sm leading-relaxed ${secondaryTextColor}`}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section ref={introRef} className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div
            className={`max-w-3xl mx-auto text-center transition-all duration-1000 transform ${introInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <h2 className={`text-3xl font-serif font-medium mb-6 ${primaryTextColor}`}>
              {t('about.intro.title', 'We Build Digital Experiences')}
            </h2>
            <div className="w-16 h-0.5 bg-moss mx-auto mb-8" />
            <p className={`text-lg ${primaryTextColor} mb-6 leading-relaxed`}>
              {t('about.intro.lead', 'Our humble origins and vision: democratizing internet access, empowering everyone to leverage technology to improve their lives.')}
            </p>
            <p className={`${secondaryTextColor} mb-6 leading-relaxed`}>
              {t('about.intro.paragraph1', 'At Riviera Tech Solutions, we specialize in delivering innovative web solutions for businesses of all sizes. Our expertise spans custom web applications, eCommerce platforms, business websites, portfolio sites, booking systems, and strategic consulting.')}
            </p>
            <p className={`${secondaryTextColor} leading-relaxed`}>
              {t('about.intro.paragraph2', 'Our team is dedicated to exceeding our clients\' expectations and driving success in every project we undertake. With a passion for technology and a commitment to excellence, we help businesses achieve their full potential in the digital landscape.')}
            </p>
          </div>
        </div>
      </section>

      {/* Founder's Story Section */}
      <section className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-serif font-medium mb-4 ${primaryTextColor}`}>
              {t('about.founder.title', 'Our Founder\'s Vision')}
            </h2>
            <div className="w-16 h-0.5 bg-moss mx-auto mb-8" />
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              {/* Image Section */}
              <div className="w-full lg:w-1/2 flex justify-center lg:justify-start">
                <div className="relative">
                  <img
                    src="/1598360880326.jpeg"
                    alt="Founder at Golden Gate Bridge"
                    className="w-80 h-96 object-cover object-center rounded-lg shadow-2xl border border-gray-700"
                    style={{
                      objectPosition: 'center 20%', // Crop to focus on the person and bridge
                      clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)' // Subtle diagonal crop
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
                </div>
              </div>

              {/* Text Section */}
              <div className="w-full lg:w-1/2">
                <p className={`${secondaryTextColor} leading-relaxed text-lg mb-6`}>
                  {t('about.founder.paragraph1', "After earning a Bachelor's degree in Electrical Engineering from San José State University and honing their skills over two years as an electronics designer and embedded systems programmer, our founder returns to Croatia with a singular mission: to ignite a digital transformation that positions the country at the vanguard of global technology.")}
                </p>
                <p className={`${secondaryTextColor} leading-relaxed text-lg mb-6`}>
                  {t('about.founder.paragraph2', "Drawing on hands-on experience with cutting-edge hardware and firmware development in Silicon Valley, they are uniquely equipped to bridge the gap between world-class innovation and Croatia's emerging tech ecosystem. By fostering strategic partnerships, mentoring local talent, and spearheading ambitious projects—ranging from IoT infrastructure to intelligent automation—they aim to cultivate an environment where creativity and collaboration thrive.")}
                </p>
                <p className={`${secondaryTextColor} leading-relaxed text-lg`}>
                  {t('about.founder.paragraph3', "With unwavering dedication and a deep commitment to the community, our founder is set to catalyze a new era of digital excellence in Croatia, inspiring the next generation of engineers and entrepreneurs to build solutions that resonate on the international stage.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section ref={missionRef} className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-serif font-medium mb-4 ${primaryTextColor}`}>
              {t('about.mission.title', 'What Drives Us')}
            </h2>
            <div className="w-16 h-0.5 bg-moss mx-auto mb-8" />
          </div>

          <div
            className={`max-w-4xl mx-auto bg-off-black rounded-lg border border-moss-deep/40 p-8 transition-all duration-1000 transform ${missionInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <p className={`${secondaryTextColor} text-lg leading-relaxed mb-6`}>
              {t('about.mission.paragraph1', "What drives us is a singular vision: to elevate Croatia to the pinnacle of technological innovation and to make our country the first destination for expertise and talent from around the world. Inspired by the trailblazing success of Rimac Automobili, we aspire to stand alongside such world-class names—bolstering the Croatian economy and cementing our nation's reputation in high tech.")}
            </p>
            <p className={`${secondaryTextColor} text-lg leading-relaxed`}>
              {t('about.mission.paragraph2', "Through relentless innovation, strategic partnerships, and the development of homegrown talent, we aim to catalyze a new era of national prosperity. By championing cutting-edge solutions and fostering an environment of excellence, we are committed to making Croatia the undisputed leader in technology.")}
            </p>
          </div>
        </div>
      </section>

      {/* What We Do — digital solutions for a better tomorrow */}
      <section ref={whatWeDoRef} className="py-20 bg-moss-night border-y border-moss-deep/40">
        <div className="container mx-auto px-4">
          <div
            className={`max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12 transition-all duration-1000 transform ${whatWeDoInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="w-full lg:w-1/2">
              <Eyebrow>{t('about.whatWeDo.eyebrow', 'What We Do')}</Eyebrow>
              <h2 className={`text-3xl md:text-4xl font-serif font-medium mb-6 ${primaryTextColor}`}>
                {t('about.whatWeDo.title', 'Digital Solutions for a Better Tomorrow')}
              </h2>
              <p className={`${secondaryTextColor} mb-8 leading-relaxed`}>
                {t('about.whatWeDo.paragraph', 'We develop custom software, platforms, and infrastructure that help organizations grow while creating positive impact for people and the planet.')}
              </p>
              <ul className="space-y-3">
                {capabilities.map((item) => (
                  <li key={item} className={`flex items-start gap-3 ${secondaryTextColor}`}>
                    <LuCheck className="w-5 h-5 text-moss-light flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-1/2">
              <img
                src={workImage}
                alt="A daylit studio of designers and engineers working beneath hanging plants, open to a green valley"
                className="w-full rounded-lg border border-moss-deep/50 shadow-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section ref={valuesRef} className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-serif font-medium mb-4 ${primaryTextColor}`}>
              {t('about.values.title', 'What We Stand For')}
            </h2>
            <div className="w-16 h-0.5 bg-moss mx-auto mb-8" />
            <p className={`${secondaryTextColor} max-w-2xl mx-auto`}>
              {t('about.values.subtitle', 'The core principles that guide our work and relationships')}
            </p>
          </div>

          <div
            className={`max-w-4xl mx-auto transition-all duration-1000 transform ${valuesInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="bg-off-black rounded-lg border border-moss-deep/40 p-8">
              <p className={`${secondaryTextColor} text-lg leading-relaxed text-center mb-6`}>
                {t('about.values.paragraph1', "We stand for unwavering justice and lasting peace, driven by a spirit of innovation that serves and uplifts. Our mission is to harness cutting-edge technology not for its own sake, but to empower communities, protect our natural world, and forge equitable opportunities for all.")}
              </p>
              <p className={`${secondaryTextColor} text-lg leading-relaxed text-center`}>
                {t('about.values.paragraph2', "By championing integrity in every decision and collaboration, we strive to build solutions that honor both people and planet—creating a future where society thrives in harmony with nature and one another.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Impact */}
      <section ref={impactRef} className="py-20 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Eyebrow>{t('about.impact.eyebrow', 'Our Impact')}</Eyebrow>
            <h2 className={`text-3xl md:text-4xl font-serif font-medium leading-snug ${primaryTextColor}`}>
              {t('about.impact.title', 'Technology can heal the world.')}
              <br />
              <span className="text-moss-light">
                {t('about.impact.titleAccent', 'We choose to build it that way.')}
              </span>
            </h2>
          </div>

          <div
            className={`max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 transition-all duration-1000 transform ${impactInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            {impact.map(({ Icon, title, text }) => (
              <div key={title} className="text-center px-2">
                <Icon className="w-9 h-9 text-moss mx-auto mb-4" aria-hidden="true" />
                <h3 className={`font-medium mb-2 ${primaryTextColor}`}>{title}</h3>
                <p className={`text-sm leading-relaxed ${secondaryTextColor}`}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section ref={ctaRef} className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ctaImage}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/65" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <div
            className={`max-w-3xl mx-auto transition-all duration-1000 transform ${ctaInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <h3 className={`text-3xl md:text-4xl font-serif font-medium mb-4 ${primaryTextColor}`}>
              {t('about.cta.title', 'Let’s Build a Future Worth Living In')}
            </h3>
            <p className={`${secondaryTextColor} text-lg mb-10 leading-relaxed`}>
              {t('about.cta.text', 'Together we can create a world of health, abundance, and harmony for all life. Tell us what you want to build.')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-block bg-moss hover:bg-moss-light text-black px-8 py-3 rounded-md text-lg font-medium transition-colors duration-300"
              >
                {t('about.cta.contactButton', 'Work With Us')}
              </Link>
              <Link
                to="/services"
                className={`inline-block border border-silver-medium hover:border-silver-light hover:bg-white/5 px-8 py-3 rounded-md text-lg font-medium transition-colors duration-300 ${primaryTextColor}`}
              >
                {t('about.cta.servicesButton', 'Our Services')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
